import { useState } from 'react'
import { Alert, Button, Card, Popconfirm, Space, Spin, Tabs, Tag, Typography } from 'antd'
import { CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ShopBillTree,
  RoomBillTree,
  countShopBillRows,
  countRoomBillLines,
} from '@/components/ShopBillTree'
import { SheetPreviewDrawer } from '@/components/SheetPreviewDrawer'
import { SpaceReviewMap } from '@/components/SpaceReviewMap'
import { SpaceReviewTable } from '@/components/SpaceReviewTable'
import { jobsApi } from '@/api/jobs'
import type { SpaceBill, SpaceReviewItem, UUID } from '@/types/api'

const { Text } = Typography

interface Props {
  jobId: UUID
  spaces: SpaceReviewItem[]
  spaceBill?: SpaceBill | null
  spaceBillLoading?: boolean
  canReview: boolean
  isFinal: boolean
  reviewPending: boolean
  updatePending: boolean
  onReviewComplete: () => void
  onShopfrontApplied?: () => void
  onUpdate: (params: {
    spaceId: string
    version: number
    updates: Partial<Pick<SpaceReviewItem, 'name' | 'space_type' | 'is_deleted'>> & {
      param_overrides?: Record<string, number>
    }
  }) => void
}

export function SpaceReviewPanel({
  jobId,
  spaces,
  spaceBill,
  spaceBillLoading = false,
  canReview,
  isFinal,
  reviewPending,
  updatePending,
  onReviewComplete,
  onShopfrontApplied,
  onUpdate,
}: Props) {
  const qc = useQueryClient()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [focusSeq, setFocusSeq] = useState(0)
  const [previewSheets, setPreviewSheets] = useState<string[] | null>(null)

  const { data: pngStatus } = useQuery({
    queryKey: ['sheet-png-status', jobId],
    queryFn: () => jobsApi.sheetPngStatus(jobId),
    refetchInterval: (q) => {
      const s = q.state.data?.status
      if (s === 'pending' || s === 'partial') return 3000
      return false
    },
  })

  const retryPng = useMutation({
    mutationFn: () => jobsApi.retrySheetPng(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet-png-status', jobId] })
      qc.invalidateQueries({ queryKey: ['sheet-previews', jobId] })
    },
  })

  const selectSpace = (key: string) => {
    setSelectedKey(key)
    setFocusSeq((n) => n + 1)
  }

  return (
    <Card
      title={
        <Space>
          <span>空间复核（{spaces.length} 个）</span>
          {canReview && <Tag color="purple">待你确认</Tag>}
          {isFinal && <Tag color="green">已完成</Tag>}
        </Space>
      }
      style={{ marginBottom: 24 }}
      extra={
        canReview && (
          <Popconfirm
            title="确认完成复核？"
            description="确认后将开始算量 + 生成报告，不能再修改空间"
            onConfirm={onReviewComplete}
          >
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={reviewPending}
            >
              完成复核，继续算量
            </Button>
          </Popconfirm>
        )
      }
    >
      {spaces.some((s) => s.parent_id) && (
        <Alert
          message="子空间面积来自平面图地面填充，不是天花外框裁切。地面工程量按子空间出，墙面/天花仍挂顶层店铺。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {pngStatus && (pngStatus.status === 'pending' || pngStatus.status === 'partial') && pngStatus.total > 0 && (
        <Alert
          message={`按图号图纸预览生成中 ${pngStatus.done}/${pngStatus.total}`}
          description={pngStatus.message || '材料行可先看图号，图片就绪后点图号打开。'}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {pngStatus && pngStatus.status === 'pending' && pngStatus.total === 0 && (
        <Alert
          message="按图号图纸预览尚未生成"
          description={
            <Space>
              <span>点材料行图号可查看；若没有图，点生成开始出图。</span>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={retryPng.isPending}
                onClick={() => retryPng.mutate()}
              >
                生成预览
              </Button>
            </Space>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {pngStatus?.status === 'failed' && (
        <Alert
          message="按图号图纸预览失败"
          description={
            <Space>
              <span>{pngStatus.message || pngStatus.warnings.join('；') || '可重试生成'}</span>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={retryPng.isPending}
                onClick={() => retryPng.mutate()}
              >
                重试
              </Button>
            </Space>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Alert
        message="怎么审核一块区域"
        description="左侧底图是 CAD 原图。点右侧空间表「定位」会放大到房间。下方有两种汇总：全店总汇总，以及按房间拆开的汇总。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 480px', minWidth: 400, maxWidth: 760 }}>
          <SpaceReviewMap
            jobId={jobId}
            selectedKey={selectedKey}
            focusSeq={focusSeq}
            onSelect={selectSpace}
            onShopfrontApplied={onShopfrontApplied}
          />
        </div>
        <div style={{ flex: '1 1 520px', minWidth: 0 }}>
          <SpaceReviewTable
            spaces={spaces}
            spaceBill={spaceBill}
            canReview={canReview}
            selectedKey={selectedKey}
            onSelect={selectSpace}
            updatePending={updatePending}
            onUpdate={onUpdate}
          />
          <div style={{ marginTop: 16 }}>
            <Text strong>装修材料 / 工艺汇总</Text>
            <div style={{ marginTop: 8 }}>
              {spaceBillLoading && !spaceBill ? (
                <Spin tip="正在汇总材料 / 工艺…">
                  <div style={{ minHeight: 72 }} />
                </Spin>
              ) : spaceBill ? (
                <Tabs
                  size="small"
                  items={[
                    {
                      key: 'shop',
                      label: `总汇总（${countShopBillRows(spaceBill)} 条）`,
                      children: <ShopBillTree bill={spaceBill} onOpenSheets={setPreviewSheets} />,
                    },
                    {
                      key: 'room',
                      label: `按房间汇总（${countRoomBillLines(spaceBill)} 条）`,
                      children: (
                        <RoomBillTree
                          bill={spaceBill}
                          selectedSpaceId={selectedKey}
                          onSelectSpace={selectSpace}
                          onOpenSheets={setPreviewSheets}
                        />
                      ),
                    },
                  ]}
                />
              ) : (
                <Text type="secondary">汇总尚未生成</Text>
              )}
            </div>
          </div>
        </div>
      </div>
      <SheetPreviewDrawer
        jobId={jobId}
        sheetNos={previewSheets || []}
        open={!!previewSheets?.length}
        onClose={() => setPreviewSheets(null)}
      />
    </Card>
  )
}
